import { useEffect, useMemo, useRef, useState, useCallback, Fragment } from "react";
import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Stage, Layer, Rect, Line, Text as KText, Image as KImage, Transformer } from "react-konva";
import useImage from "use-image";
import api from "@/lib/api";
import { toast } from "sonner";
import AssetsLibrary from "@/components/AssetsLibrary";
import {
  Type, Image as ImageIcon, Shapes, Palette, Layers as LayersIcon,
  Plus, Trash2, Copy, ChevronsUp, ChevronsDown, Download, ArrowLeft, Save,
  Square, Video as VideoIcon,
  Wand2, Loader2, Undo2, Redo2,
} from "lucide-react";

// ============== Konva helper — image element ==============
function KonvaImg({ src, ...rest }) {
  const [img] = useImage(src, "anonymous");
  return <KImage image={img} {...rest} />;
}

// ============== Video element (rendered outside stage as overlay) ==============
function VideoOverlay({ el, scale, selected, onSelect }) {
  return (
    <div
      onClick={onSelect}
      className={"absolute pointer-events-auto cursor-pointer " + (selected ? "outline outline-2 outline-blue-500" : "")}
      style={{
        left: el.x * scale, top: el.y * scale,
        width: el.width * scale, height: el.height * scale,
        transform: `rotate(${el.rotation || 0}deg)`, transformOrigin: "top left",
      }}
    >
      <video src={el.props?.src} controls={el.props?.controls !== false} autoPlay={!!el.props?.autoplay} loop muted={!!el.props?.muted} className="w-full h-full object-cover rounded" />
    </div>
  );
}

// ============== Fonts + text presets ==============
const FONTS = [
  "Manrope", "Inter", "Arial", "Georgia", "Times New Roman",
  "Courier New", "Verdana", "Trebuchet MS", "Impact", "Comic Sans MS",
];

const TEXT_PRESETS = [
  { label: "Add heading",    props: { text: "Add a heading",    size: 72, weight: 900, font: "Manrope", color: "#0B0D17" }, sample: "Heading" },
  { label: "Add subheading", props: { text: "Add a subheading", size: 42, weight: 700, font: "Manrope", color: "#0B0D17" }, sample: "Subheading" },
  { label: "Add body text",  props: { text: "Add body text",    size: 22, weight: 400, font: "Manrope", color: "#4A4D57" }, sample: "Body text" },
  { label: "Add caption",    props: { text: "Small caption",    size: 14, weight: 500, font: "Manrope", color: "#747680" }, sample: "Caption" },
  { label: "Editorial",      props: { text: "Editorial title",  size: 60, weight: 700, font: "Georgia",  color: "#0B0D17" }, sample: "Editorial" },
  { label: "Bold impact",    props: { text: "IMPACT",           size: 84, weight: 900, font: "Impact",   color: "#0B5CFF" }, sample: "IMPACT" },
];

// ============== Main editor ==============
const DEFAULT_PAGE = {
  id: "p1", name: "Page 1", width: 1200, height: 675,
  background: "#FFFFFF", background_image: null, elements: [],
};

// Snap threshold in canvas coords (i.e. after unscaling display px)
const SNAP_THRESHOLD = 8;

export default function GraphicDesigner() {
  const { id: designId } = useParams();
  const nav = useNavigate();
  const [design, setDesign] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const selectedId = selectedIds[selectedIds.length - 1] || null;
  const setSelectedId = useCallback((id) => setSelectedIds(id ? [id] : []), []);
  const dragStartRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(!designId);
  const [tool, setTool] = useState("select");
  const [brandKits, setBrandKits] = useState([]);
  const [savingBg, setSavingBg] = useState(false);
  const [guides, setGuides] = useState({ v: [], h: [] });
  const stageRef = useRef(null);
  const trRef = useRef(null);

  // ---- History (undo / redo) ----
  const historyRef = useRef([]);       // list of pages snapshots (JSON)
  const histIdxRef = useRef(-1);       // current index
  const [histTick, setHistTick] = useState(0); // for re-render of buttons

  const commit = useCallback((pages) => {
    // Push a snapshot to the history stack (trims redo tail)
    const snap = JSON.stringify(pages);
    if (historyRef.current[histIdxRef.current] === snap) return;
    historyRef.current = historyRef.current.slice(0, histIdxRef.current + 1);
    historyRef.current.push(snap);
    // cap to 60 snapshots
    if (historyRef.current.length > 60) {
      historyRef.current = historyRef.current.slice(historyRef.current.length - 60);
    }
    histIdxRef.current = historyRef.current.length - 1;
    setHistTick((t) => t + 1);
  }, []);

  const undo = useCallback(() => {
    if (histIdxRef.current <= 0) return;
    histIdxRef.current -= 1;
    const pages = JSON.parse(historyRef.current[histIdxRef.current]);
    setDesign((d) => (d ? { ...d, pages } : d));
    setActiveIdx((i) => Math.max(0, Math.min(i, pages.length - 1)));
    setSelectedId(null);
    setHistTick((t) => t + 1);
  }, []);

  const redo = useCallback(() => {
    if (histIdxRef.current >= historyRef.current.length - 1) return;
    histIdxRef.current += 1;
    const pages = JSON.parse(historyRef.current[histIdxRef.current]);
    setDesign((d) => (d ? { ...d, pages } : d));
    setActiveIdx((i) => Math.max(0, Math.min(i, pages.length - 1)));
    setSelectedId(null);
    setHistTick((t) => t + 1);
  }, []);

  // Load design or create new
  useEffect(() => {
    api.get("/designs/templates/list").then((r) => setTemplates(r.data));
    api.get("/brand-kits").then((r) => setBrandKits(Array.isArray(r.data) ? r.data : (r.data?.brand_kits || []))).catch(() => {});
    if (designId) {
      api.get(`/designs/${designId}`).then((r) => {
        setDesign(r.data);
        historyRef.current = [JSON.stringify(r.data.pages)];
        histIdxRef.current = 0;
        setHistTick((t) => t + 1);
      });
    } else {
      const seed = { title: "Untitled design", kind: "social", pages: [{ ...DEFAULT_PAGE }] };
      setDesign(seed);
      historyRef.current = [JSON.stringify(seed.pages)];
      histIdxRef.current = 0;
    }
  }, [designId]);

  // Attach transformer to selected node(s)
  useEffect(() => {
    if (!trRef.current || !stageRef.current) return;
    const stage = stageRef.current;
    if (selectedIds.length === 0) { trRef.current.nodes([]); trRef.current.getLayer()?.batchDraw(); return; }
    const nodes = selectedIds.map((id) => stage.findOne(`#${id}`)).filter(Boolean);
    trRef.current.nodes(nodes);
    trRef.current.getLayer()?.batchDraw();
  }, [selectedIds, design]);

  // Global keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toUpperCase();
      const inField = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target?.isContentEditable;
      // Undo / Redo — don't hijack when typing in an input/textarea
      if (!inField && (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z") { e.preventDefault(); undo(); return; }
      if (!inField && (e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) { e.preventDefault(); redo(); return; }
      // Delete selected (only when not typing in a field)
      if (!inField && (e.key === "Delete" || e.key === "Backspace") && selectedIds.length > 0) {
        e.preventDefault();
        setDesign((d) => {
          if (!d) return d;
          const pages = [...d.pages];
          const cur = pages[activeIdx];
          pages[activeIdx] = { ...cur, elements: (cur.elements || []).filter((el) => !selectedIds.includes(el.id)) };
          commit(pages);
          return { ...d, pages };
        });
        setSelectedIds([]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo, selectedIds, activeIdx, commit]);

  const page = design?.pages?.[activeIdx] || DEFAULT_PAGE;
  const selected = page.elements?.find((e) => e.id === selectedId);

  const updatePage = (patch, { pushHistory = true } = {}) => {
    setDesign((d) => {
      const pages = [...d.pages];
      pages[activeIdx] = { ...pages[activeIdx], ...patch };
      if (pushHistory) commit(pages);
      return { ...d, pages };
    });
  };

  const updateElement = (id, patch, opts) => {
    updatePage({ elements: page.elements.map((e) => (e.id === id ? { ...e, ...patch, props: { ...e.props, ...(patch.props || {}) } } : e)) }, opts);
  };

  const addElement = (el) => {
    const id = "el_" + Math.random().toString(36).slice(2, 9);
    updatePage({ elements: [...page.elements, { ...el, id, z: page.elements.length + 1 }] });
    setSelectedId(id);
  };

  const removeElement = (id) => {
    updatePage({ elements: page.elements.filter((e) => e.id !== id) });
    setSelectedId(null);
  };

  const duplicateElement = (id) => {
    const el = page.elements.find((e) => e.id === id);
    if (!el) return;
    addElement({ ...el, x: (el.x || 0) + 20, y: (el.y || 0) + 20 });
  };

  const raiseElement = (id, dir = "up") => {
    const els = [...page.elements];
    const i = els.findIndex((e) => e.id === id);
    if (i < 0) return;
    const j = dir === "up" ? Math.min(els.length - 1, i + 1) : Math.max(0, i - 1);
    if (i === j) return;
    [els[i], els[j]] = [els[j], els[i]];
    updatePage({ elements: els.map((e, k) => ({ ...e, z: k + 1 })) });
  };

  // ---- Page ops ----
  // Compute page arrays OUTSIDE the setDesign updater so React 18 StrictMode
  // double-invoke doesn't double-push history snapshots.
  const addPage = () => {
    if (!design) return;
    const newPage = { ...DEFAULT_PAGE, id: "p_" + Math.random().toString(36).slice(2, 8), name: `Page ${design.pages.length + 1}` };
    const pages = [...design.pages, newPage];
    commit(pages);
    setDesign({ ...design, pages });
    setActiveIdx(pages.length - 1);
  };
  const duplicatePage = () => {
    if (!design) return;
    const src = design.pages[activeIdx];
    if (!src) return;
    const copy = JSON.parse(JSON.stringify(src));
    copy.id = "p_" + Math.random().toString(36).slice(2, 8);
    copy.name = `${copy.name || "Page"} (copy)`;
    const pages = [...design.pages];
    pages.splice(activeIdx + 1, 0, copy);
    commit(pages);
    setDesign({ ...design, pages });
  };
  const removePage = () => {
    if (!design) return;
    if (design.pages.length <= 1) return toast.error("Design needs at least one page");
    if (!design.pages[activeIdx]) return;
    const pages = design.pages.filter((_, i) => i !== activeIdx);
    commit(pages);
    setDesign({ ...design, pages });
    setActiveIdx((i) => Math.max(0, Math.min(i - 1, pages.length - 1)));
  };

  // ---- Template starter ----
  const startFromTemplate = (t) => {
    const seed = { title: t.title, kind: t.kind, pages: [{ ...t.page, id: "p1" }] };
    setDesign(seed);
    historyRef.current = [JSON.stringify(seed.pages)];
    histIdxRef.current = 0;
    setHistTick((v) => v + 1);
    setShowTemplates(false);
  };

  // ---- Save ----
  const save = async () => {
    try {
      if (designId) {
        const { data } = await api.patch(`/designs/${designId}`, { title: design.title, pages: design.pages });
        setDesign(data);
        toast.success("Saved");
      } else {
        const { data } = await api.post("/designs", { title: design.title, kind: design.kind, pages: design.pages });
        toast.success("Saved");
        nav(`/app/designer/${data.id}`, { replace: true });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  // ---- Export ----
  const exportPNG = () => {
    if (!stageRef.current) return;
    setSelectedId(null);
    setGuides({ v: [], h: [] });
    // wait a frame so transformer/guides are cleared
    setTimeout(() => {
      const uri = stageRef.current.toDataURL({ pixelRatio: 2 });
      const a = document.createElement("a");
      a.download = `${design?.title || "design"}-p${activeIdx + 1}.png`;
      a.href = uri; a.click();
    }, 50);
  };

  // ---- Apply brand kit ----
  const applyBrandKit = (kit) => {
    // The API returns `primary`, `accent`, `font_heading`. Support the older
    // *_color / font_family shape too for forward-compat.
    const primary = kit.primary || kit.primary_color;
    const accent  = kit.accent  || kit.accent_color;
    const font    = kit.font_heading || kit.font_family;
    updatePage({
      background: primary || page.background,
      elements: page.elements.map((e) => {
        if (e.kind === "text") return { ...e, props: { ...e.props, color: e.props?.color === "#FFFFFF" ? (accent || e.props.color) : e.props.color, font: font || e.props.font } };
        if (e.kind === "shape") return { ...e, props: { ...e.props, fill: accent || e.props.fill } };
        return e;
      }),
    });
    toast.success(`Applied brand kit: ${kit.name}`);
  };

  // ---- AI: remove background ----
  const removeBg = async () => {
    if (!selected || selected.kind !== "image") return toast.error("Select an image first");
    setSavingBg(true);
    try {
      // The AI endpoint fetches the reference image server-side via httpx — a
      // relative /api/public/files/… URL won't resolve, so send an absolute one.
      let imageUrl = selected.props?.src || "";
      if (imageUrl.startsWith("/")) imageUrl = window.location.origin + imageUrl;
      const { data } = await api.post("/thumbnail-studio/ai/remove-bg", { image_url: imageUrl });
      const nextSrc = data.image_b64 ? `data:image/png;base64,${data.image_b64}` : (data.url || data.image_url);
      if (!nextSrc) throw new Error("No image returned");
      updateElement(selected.id, { props: { src: nextSrc } });
      toast.success("Background removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Background removal failed");
    } finally {
      setSavingBg(false);
    }
  };

  // ---- Stage rendering ----
  const stageWidth = 800; // display width
  const scale = stageWidth / (page.width || 1200);
  const stageHeight = (page.height || 675) * scale;

  // ---- Snap logic ----
  const computeSnap = (dragEl, x, y) => {
    // In canvas coords (unscaled). Returns { x, y, guides } with snapped position.
    const w = dragEl.width || 0, h = dragEl.height || 0;
    const canvasW = page.width || 1200, canvasH = page.height || 675;
    // targets: canvas center + edges + every other element's edges/centers
    const vTargets = [0, canvasW / 2, canvasW];
    const hTargets = [0, canvasH / 2, canvasH];
    for (const other of page.elements || []) {
      if (other.id === dragEl.id || other.kind === "video") continue;
      const ox = other.x || 0, oy = other.y || 0, ow = other.width || 0, oh = other.height || 0;
      vTargets.push(ox, ox + ow / 2, ox + ow);
      hTargets.push(oy, oy + oh / 2, oy + oh);
    }

    const candidates = { x: [x, x + w / 2, x + w], y: [y, y + h / 2, y + h] };
    let dx = null, dy = null;
    const shownV = [], shownH = [];

    for (let i = 0; i < candidates.x.length; i++) {
      for (const t of vTargets) {
        const diff = t - candidates.x[i];
        if (Math.abs(diff) < SNAP_THRESHOLD) {
          if (dx === null || Math.abs(diff) < Math.abs(dx)) dx = diff;
          if (Math.abs(diff) < SNAP_THRESHOLD) shownV.push(t);
        }
      }
    }
    for (let i = 0; i < candidates.y.length; i++) {
      for (const t of hTargets) {
        const diff = t - candidates.y[i];
        if (Math.abs(diff) < SNAP_THRESHOLD) {
          if (dy === null || Math.abs(diff) < Math.abs(dy)) dy = diff;
          if (Math.abs(diff) < SNAP_THRESHOLD) shownH.push(t);
        }
      }
    }
    return {
      x: dx !== null ? x + dx : x,
      y: dy !== null ? y + dy : y,
      v: Array.from(new Set(shownV)),
      h: Array.from(new Set(shownH)),
    };
  };

  const renderElement = (el) => {
    const common = {
      id: el.id, x: el.x, y: el.y, rotation: el.rotation || 0, opacity: el.opacity ?? 1,
      draggable: true,
      onClick: (e) => {
        const shift = e.evt?.shiftKey || e.evt?.metaKey;
        if (shift) setSelectedIds((prev) => prev.includes(el.id) ? prev.filter((x) => x !== el.id) : [...prev, el.id]);
        else setSelectedIds([el.id]);
      },
      onTap: (e) => {
        const shift = e.evt?.shiftKey || e.evt?.metaKey;
        if (shift) setSelectedIds((prev) => prev.includes(el.id) ? prev.filter((x) => x !== el.id) : [...prev, el.id]);
        else setSelectedIds([el.id]);
      },
      onDragStart: () => {
        // Snapshot start positions of all currently-selected elements so we can move them as a group.
        if (selectedIds.length > 1 && selectedIds.includes(el.id)) {
          dragStartRef.current = {
            anchor: el.id,
            positions: Object.fromEntries(
              (page.elements || [])
                .filter((o) => selectedIds.includes(o.id))
                .map((o) => [o.id, { x: o.x || 0, y: o.y || 0 }])
            ),
          };
        } else {
          dragStartRef.current = null;
        }
      },
      dragBoundFunc: function (pos) {
        const cx = pos.x / scale, cy = pos.y / scale;
        const snapped = computeSnap(el, cx, cy);
        setGuides({ v: snapped.v, h: snapped.h });
        return { x: snapped.x * scale, y: snapped.y * scale };
      },
      onDragEnd: (e) => {
        setGuides({ v: [], h: [] });
        const ds = dragStartRef.current;
        if (ds && ds.anchor === el.id) {
          // Move the entire selection by the same delta the anchor moved.
          const anchorStart = ds.positions[el.id];
          const dx = e.target.x() - anchorStart.x;
          const dy = e.target.y() - anchorStart.y;
          updatePage({
            elements: (page.elements || []).map((o) => (
              selectedIds.includes(o.id) && ds.positions[o.id]
                ? { ...o, x: ds.positions[o.id].x + dx, y: ds.positions[o.id].y + dy }
                : o
            )),
          });
        } else {
          updateElement(el.id, { x: e.target.x(), y: e.target.y() });
        }
        dragStartRef.current = null;
      },
      onTransformEnd: (e) => {
        const node = e.target;
        updateElement(el.id, {
          x: node.x(), y: node.y(),
          width: Math.max(20, node.width() * node.scaleX()),
          height: Math.max(20, node.height() * node.scaleY()),
          rotation: node.rotation(),
        });
        node.scaleX(1); node.scaleY(1);
      },
    };
    if (el.kind === "text") {
      return (
        <KText key={el.id} {...common}
          width={el.width} height={el.height}
          text={el.props?.text || "Text"} fontSize={el.props?.size || 32}
          fontFamily={el.props?.font || "Manrope"} fontStyle={el.props?.weight >= 700 ? "bold" : "normal"}
          fill={el.props?.color || "#0B0D17"} align={el.props?.align || "left"}
          verticalAlign={el.props?.verticalAlign || "top"}
          lineHeight={el.props?.lineHeight || 1.2}
        />
      );
    }
    if (el.kind === "image") {
      return <KonvaImg key={el.id} {...common} width={el.width} height={el.height} src={el.props?.src} />;
    }
    if (el.kind === "shape") {
      const fill = el.props?.fill || "#0B5CFF";
      return (
        <Rect key={el.id} {...common} width={el.width} height={el.height} fill={fill} cornerRadius={el.props?.radius ?? 0} />
      );
    }
    return null;
  };

  if (!design) return <div className="p-10">Loading…</div>;

  const canUndo = histIdxRef.current > 0;
  const canRedo = histIdxRef.current < historyRef.current.length - 1;

  return (
    <div className="fixed inset-0 flex flex-col bg-[#0F1220] text-white" data-testid="graphic-designer">
      {/* TOP BAR */}
      <div className="h-14 border-b border-white/8 flex items-center px-4 gap-3">
        <Link to="/app/studio" className="text-white/70 hover:text-white text-sm inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</Link>
        <input
          value={design.title}
          onChange={(e) => setDesign({ ...design, title: e.target.value })}
          className="bg-transparent px-2 py-1 rounded font-semibold text-sm outline-none focus:bg-white/8 max-w-xs"
          data-testid="design-title"
        />
        <div className="ml-3 flex items-center gap-1">
          <button onClick={undo} disabled={!canUndo} title="Undo (Ctrl+Z)"
                  className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent"
                  data-testid="undo-btn">
            <Undo2 className="w-4 h-4" />
          </button>
          <button onClick={redo} disabled={!canRedo} title="Redo (Ctrl+Y / Ctrl+Shift+Z)"
                  className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent"
                  data-testid="redo-btn">
            <Redo2 className="w-4 h-4" />
          </button>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={save} className="px-4 py-1.5 rounded-full bg-white text-[#0B0D17] text-sm font-bold hover:scale-[1.03] transition-transform inline-flex items-center gap-1.5" data-testid="save-design"><Save className="w-3.5 h-3.5" /> Save</button>
          <button onClick={exportPNG} className="px-4 py-1.5 rounded-full bg-[#0B5CFF] text-white text-sm font-bold hover:bg-[#0A4FD9] transition-colors inline-flex items-center gap-1.5" data-testid="export-png"><Download className="w-3.5 h-3.5" /> Export PNG</button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* LEFT SIDEBAR — element library */}
        <div className="w-16 border-r border-white/8 flex flex-col items-center py-3 gap-1">
          <SidebarBtn icon={LayersIcon} label="Templates" active={tool === "templates"} onClick={() => { setTool("templates"); setShowTemplates(true); }} />
          <SidebarBtn icon={Type}       label="Text"      active={tool === "text"}      onClick={() => setTool("text")} />
          <SidebarBtn icon={ImageIcon}  label="Image"     active={tool === "image"}     onClick={() => setTool("image")} />
          <SidebarBtn icon={VideoIcon}  label="Video"     active={tool === "video"}     onClick={() => setTool("video")} />
          <SidebarBtn icon={Shapes}     label="Shapes"    active={tool === "shapes"}    onClick={() => setTool("shapes")} />
          <SidebarBtn icon={Palette}    label="Brand"     active={tool === "brand"}     onClick={() => setTool("brand")} />
        </div>

        {/* PANEL — depending on tool */}
        <div className="w-72 border-r border-white/8 overflow-y-auto bg-[#161A2E]">
          {tool === "text" && (
            <div className="p-4 space-y-3" data-testid="text-panel">
              <div className="text-xs uppercase tracking-widest text-white/50 font-bold">Add text</div>
              <div className="grid grid-cols-1 gap-2">
                {TEXT_PRESETS.map((p) => (
                  <button
                    key={p.label}
                    onClick={() => {
                      // Stagger consecutive presets so they don't stack exactly on top of each other.
                      const n = (page.elements || []).length;
                      const offset = (n % 8) * 30;
                      addElement({ kind: "text", x: 60 + offset, y: 60 + offset, width: 600, height: Math.round((p.props.size || 32) * 1.5), props: { ...p.props, align: "left" } });
                    }}
                    className="flex items-center justify-between rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#22D3EE] p-3 text-left transition-colors"
                    data-testid={`preset-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span
                      className="text-white truncate"
                      style={{ fontFamily: p.props.font, fontSize: Math.min(28, p.props.size / 3), fontWeight: p.props.weight, color: "#F5F5F7" }}
                    >{p.sample}</span>
                    <Plus className="w-4 h-4 text-white/40" />
                  </button>
                ))}
              </div>
              <div className="text-xs text-white/40 pt-2">Tip: Delete key removes the selected element. Ctrl+Z to undo.</div>
            </div>
          )}
          {tool === "image" && (
            <AssetsLibrary kind="image" onPick={(url) => {
              if (selected && selected.kind === "image") {
                updateElement(selected.id, { props: { src: url } });
                toast.success("Image replaced");
              } else {
                addElement({ kind: "image", x: 100, y: 100, width: 400, height: 300, props: { src: url } });
              }
            }} />
          )}
          {tool === "video" && (
            <AssetsLibrary kind="video" onPick={(url) => {
              if (selected && selected.kind === "video") {
                updateElement(selected.id, { props: { ...selected.props, src: url } });
                toast.success("Video replaced");
              } else {
                addElement({ kind: "video", x: 60, y: 60, width: 480, height: 270, props: { src: url, controls: true, muted: true } });
              }
            }} />
          )}
          {tool === "shapes" && (
            <div className="p-4 space-y-3">
              <div className="text-xs uppercase tracking-widest text-white/50 font-bold">Shapes</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "Square", icon: Square, radius: 0 },
                  { label: "Rounded", icon: Square, radius: 24 },
                  { label: "Pill", icon: Square, radius: 999 },
                ].map((s) => (
                  <button key={s.label}
                    onClick={() => addElement({ kind: "shape", x: 100, y: 100, width: 200, height: 120, props: { shape: "rect", fill: "#0B5CFF", radius: s.radius } })}
                    className="aspect-square rounded-lg bg-white/5 hover:bg-white/10 flex flex-col items-center justify-center gap-1 text-xs text-white/70">
                    <s.icon className="w-6 h-6" /> {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {tool === "brand" && (
            <div className="p-4 space-y-3">
              <div className="text-xs uppercase tracking-widest text-white/50 font-bold">Apply brand kit</div>
              {brandKits.length === 0 && <div className="text-sm text-white/50">No brand kits yet — create one from the Brand Kits page.</div>}
              {brandKits.map((k) => (
                <button key={k.id} onClick={() => applyBrandKit(k)} className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 text-left" data-testid={`apply-brand-${k.id}`}>
                  <span className="w-8 h-8 rounded-lg" style={{ background: k.primary || k.primary_color }} />
                  <span className="w-8 h-8 rounded-lg" style={{ background: k.accent || k.accent_color }} />
                  <span className="ml-1 text-sm">{k.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* CANVAS */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-auto flex items-center justify-center p-8 bg-[#0B0D17]">
            <div className="relative shadow-2xl" style={{ background: page.background, width: stageWidth, height: stageHeight }}>
              <Stage ref={stageRef} width={stageWidth} height={stageHeight} scaleX={scale} scaleY={scale} onClick={(e) => { if (e.target === e.target.getStage()) setSelectedId(null); }}>
                <Layer>
                  {(page.elements || []).filter((e) => e.kind !== "video").map(renderElement)}
                  {/* Snap guides */}
                  {guides.v.map((x, i) => (
                    <React.Fragment key={`gv-${i}`}>
                      <Line points={[x, 0, x, page.height || 675]} stroke="#EC4899" strokeWidth={1 / scale} dash={[6 / scale, 4 / scale]} listening={false} />
                      <KText x={x + 4 / scale} y={4 / scale} text={`${Math.round(x)}px`} fontSize={11 / scale} fill="#EC4899" fontStyle="bold" listening={false} />
                    </React.Fragment>
                  ))}
                  {guides.h.map((y, i) => (
                    <React.Fragment key={`gh-${i}`}>
                      <Line points={[0, y, page.width || 1200, y]} stroke="#EC4899" strokeWidth={1 / scale} dash={[6 / scale, 4 / scale]} listening={false} />
                      <KText x={4 / scale} y={y + 4 / scale} text={`${Math.round(y)}px`} fontSize={11 / scale} fill="#EC4899" fontStyle="bold" listening={false} />
                    </React.Fragment>
                  ))}
                  {selectedIds.length > 0 && <Transformer ref={trRef} rotateEnabled boundBoxFunc={(oldB, newB) => (newB.width < 20 || newB.height < 20 ? oldB : newB)} />}
                </Layer>
              </Stage>
              {/* Video overlays */}
              <div className="absolute inset-0 pointer-events-none">
                {(page.elements || []).filter((e) => e.kind === "video").map((el) => (
                  <VideoOverlay key={el.id} el={el} scale={scale} selected={selectedId === el.id} onSelect={() => setSelectedId(el.id)} />
                ))}
              </div>
            </div>
          </div>

          {/* PAGES FILMSTRIP */}
          <div className="h-24 border-t border-white/8 bg-[#0F1220] flex items-center gap-2 px-4 overflow-x-auto">
            {design.pages.map((p, i) => (
              <button key={p.id}
                      onClick={() => { setActiveIdx(i); setSelectedId(null); }}
                      className={"shrink-0 h-16 aspect-video rounded-lg border-2 flex items-center justify-center text-xs " +
                        (i === activeIdx ? "border-[#22D3EE] bg-white/10" : "border-white/10 bg-white/5 hover:bg-white/10")}
                      data-testid={`page-thumb-${i}`}>
                {p.name || `Page ${i + 1}`}
              </button>
            ))}
            <button onClick={addPage} className="shrink-0 h-16 w-16 rounded-lg border-2 border-dashed border-white/20 hover:border-[#22D3EE] flex items-center justify-center" data-testid="add-page">
              <Plus className="w-5 h-5 text-white/70" />
            </button>
            <div className="ml-auto flex items-center gap-1">
              <button onClick={duplicatePage} title="Duplicate page" className="p-2 rounded-lg hover:bg-white/10"><Copy className="w-4 h-4 text-white/70" /></button>
              <button onClick={removePage}    title="Delete page"    className="p-2 rounded-lg hover:bg-red-500/20"><Trash2 className="w-4 h-4 text-red-400" /></button>
            </div>
          </div>
        </div>

        {/* RIGHT INSPECTOR */}
        <div className="w-72 border-l border-white/8 bg-[#161A2E] overflow-y-auto">
          {selectedIds.length > 1 ? (
            <div className="p-4 space-y-3" data-testid="multi-select-inspector">
              <div className="font-bold text-white/70 uppercase text-xs tracking-widest">Group · {selectedIds.length} selected</div>
              <div className="text-xs text-white/50">Shift-click to add/remove. Drag any one — they all move together.</div>
              <div className="grid grid-cols-2 gap-1">
                <button
                  onClick={() => {
                    setDesign((d) => {
                      const pages = [...d.pages];
                      const cur = pages[activeIdx];
                      pages[activeIdx] = { ...cur, elements: (cur.elements || []).filter((el) => !selectedIds.includes(el.id)) };
                      commit(pages);
                      return { ...d, pages };
                    });
                    setSelectedIds([]);
                  }}
                  className="text-xs p-2 rounded bg-red-500/20 text-red-300 hover:bg-red-500/30 inline-flex items-center gap-1 justify-center col-span-2"
                  data-testid="delete-group"
                >
                  <Trash2 className="w-3 h-3" /> Delete all
                </button>
                <button
                  onClick={() => setSelectedIds([])}
                  className="text-xs p-2 rounded bg-white/5 hover:bg-white/10 inline-flex items-center gap-1 justify-center col-span-2"
                >Clear selection</button>
              </div>
            </div>
          ) : !selected ? (
            <div className="p-4 text-sm text-white/50">
              <div className="font-bold text-white/70 mb-2">Canvas</div>
              <label className="block mt-3 text-xs uppercase tracking-widest font-bold text-white/50">Background</label>
              <input type="color" value={page.background} onChange={(e) => updatePage({ background: e.target.value })} className="mt-1 w-full h-10 rounded" />
              <label className="block mt-4 text-xs uppercase tracking-widest font-bold text-white/50">Canvas size</label>
              <div className="flex gap-2 mt-1">
                <input type="number" value={page.width} onChange={(e) => updatePage({ width: +e.target.value })} className="flex-1 h-9 px-2 rounded bg-white/10 text-white" placeholder="width" />
                <input type="number" value={page.height} onChange={(e) => updatePage({ height: +e.target.value })} className="flex-1 h-9 px-2 rounded bg-white/10 text-white" placeholder="height" />
              </div>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              <div className="font-bold text-white/70 uppercase text-xs tracking-widest">{selected.kind} · Inspector</div>
              {selected.kind === "text" && (
                <>
                  <textarea value={selected.props?.text || ""} onChange={(e) => updateElement(selected.id, { props: { text: e.target.value } })} className="w-full p-2 rounded bg-white/10 text-sm text-white outline-none" rows={3} placeholder="Text" />
                  <label className="text-xs text-white/50 block">Font
                    <select
                      value={selected.props?.font || "Manrope"}
                      onChange={(e) => updateElement(selected.id, { props: { font: e.target.value } })}
                      className="mt-1 w-full h-9 px-2 rounded bg-white/10 text-white text-sm outline-none"
                      style={{ fontFamily: selected.props?.font || "Manrope" }}
                      data-testid="font-select"
                    >
                      {FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
                    </select>
                  </label>
                  <label className="text-xs text-white/50">Font size <input type="range" min="10" max="200" value={selected.props?.size || 32} onChange={(e) => updateElement(selected.id, { props: { size: +e.target.value } })} className="w-full" /></label>
                  <label className="text-xs text-white/50">Weight <input type="range" min="300" max="900" step="100" value={selected.props?.weight || 400} onChange={(e) => updateElement(selected.id, { props: { weight: +e.target.value } })} className="w-full" /></label>
                  <label className="text-xs text-white/50">Line height <input type="range" min="0.8" max="2" step="0.05" value={selected.props?.lineHeight || 1.2} onChange={(e) => updateElement(selected.id, { props: { lineHeight: +e.target.value } })} className="w-full" data-testid="lineheight-range" /></label>
                  <label className="text-xs text-white/50 block">Color <input type="color" value={selected.props?.color || "#0B0D17"} onChange={(e) => updateElement(selected.id, { props: { color: e.target.value } })} className="mt-1 w-full h-8 rounded" /></label>
                  <div className="grid grid-cols-3 gap-1">
                    {["left", "center", "right"].map((a) => (
                      <button key={a} onClick={() => updateElement(selected.id, { props: { align: a } })} className={"p-2 rounded text-xs " + (selected.props?.align === a ? "bg-[#22D3EE]/25 text-[#22D3EE]" : "bg-white/5 text-white/70")}>{a}</button>
                    ))}
                  </div>
                </>
              )}
              {selected.kind === "shape" && (
                <>
                  <label className="text-xs text-white/50 block">Fill <input type="color" value={selected.props?.fill || "#0B5CFF"} onChange={(e) => updateElement(selected.id, { props: { fill: e.target.value } })} className="mt-1 w-full h-8 rounded" /></label>
                  <label className="text-xs text-white/50">Corner radius <input type="range" min="0" max="200" value={selected.props?.radius || 0} onChange={(e) => updateElement(selected.id, { props: { radius: +e.target.value } })} className="w-full" /></label>
                </>
              )}
              {selected.kind === "image" && (
                <>
                  <input value={selected.props?.src || ""} onChange={(e) => updateElement(selected.id, { props: { src: e.target.value } })} className="w-full p-2 rounded bg-white/10 text-xs text-white" placeholder="Image URL" />
                  <button onClick={removeBg} disabled={savingBg} className="w-full flex items-center gap-2 justify-center p-2 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white text-xs font-bold hover:opacity-90 disabled:opacity-50" data-testid="ai-remove-bg">
                    {savingBg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Remove background (AI)
                  </button>
                </>
              )}
              <div className="pt-2 border-t border-white/10 space-y-2">
                <label className="text-xs text-white/50">Opacity <input type="range" min="0" max="1" step="0.05" value={selected.opacity ?? 1} onChange={(e) => updateElement(selected.id, { opacity: +e.target.value })} className="w-full" /></label>
                <div className="grid grid-cols-2 gap-1">
                  <button onClick={() => raiseElement(selected.id, "up")}   className="text-xs p-2 rounded bg-white/5 hover:bg-white/10 inline-flex items-center gap-1 justify-center"><ChevronsUp className="w-3 h-3" /> Forward</button>
                  <button onClick={() => raiseElement(selected.id, "down")} className="text-xs p-2 rounded bg-white/5 hover:bg-white/10 inline-flex items-center gap-1 justify-center"><ChevronsDown className="w-3 h-3" /> Back</button>
                  <button onClick={() => duplicateElement(selected.id)}     className="text-xs p-2 rounded bg-white/5 hover:bg-white/10 inline-flex items-center gap-1 justify-center"><Copy className="w-3 h-3" /> Duplicate</button>
                  <button onClick={() => removeElement(selected.id)} className="text-xs p-2 rounded bg-red-500/20 text-red-300 hover:bg-red-500/30 inline-flex items-center gap-1 justify-center" data-testid="delete-element"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* TEMPLATES MODAL */}
      {showTemplates && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-8"
          data-testid="templates-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setShowTemplates(false); }}
          onKeyDown={(e) => { if (e.key === "Escape") setShowTemplates(false); }}
          tabIndex={-1}
          ref={(el) => { if (el && showTemplates) el.focus(); }}
        >
          <div className="max-w-4xl w-full bg-[#161A2E] rounded-2xl border border-white/10 p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display font-black text-2xl">Start from a template</h2>
              <button onClick={() => setShowTemplates(false)} className="text-white/60 hover:text-white text-sm" aria-label="Close templates" data-testid="close-templates">Skip →</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {templates.map((t) => (
                <button key={t.id} onClick={() => startFromTemplate(t)} className="text-left group" data-testid={`template-${t.id}`}>
                  <div className="aspect-video rounded-xl border border-white/10 flex items-center justify-center text-xs text-white/50 group-hover:border-[#22D3EE] transition-colors" style={{ background: t.page.background }}>
                    {t.page.background_image ? <img src={t.page.background_image} className="w-full h-full object-cover rounded-xl" /> : t.title}
                  </div>
                  <div className="text-xs text-white/70 mt-2 font-semibold">{t.title}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const SidebarBtn = ({ icon: Icon, label, active, onClick }) => (
  <button onClick={onClick} title={label}
          className={"w-12 h-12 rounded-lg flex flex-col items-center justify-center gap-0.5 transition-colors " +
                     (active ? "bg-[#22D3EE]/20 text-[#22D3EE]" : "text-white/60 hover:bg-white/5 hover:text-white")}
          data-testid={`sidebar-${label.toLowerCase()}`}>
    <Icon className="w-4 h-4" />
    <span className="text-[9px] font-bold uppercase tracking-wider">{label}</span>
  </button>
);
